import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import api from '../../services/api';
import { Modal } from '../Common/Modal';
import Alert from '../Common/Alert';
import './AddInvestigatorModal.css';

/** Build vPhone value for InvestigatorMst: "+91 9974410056" */
function buildVPhone(dialDigits, localDigits) {
  const dial = String(dialDigits || '').replace(/\D/g, '');
  const local = String(localDigits || '').replace(/\D/g, '');
  if (!dial && !local) return null;
  let s;
  if (dial && local) s = `+${dial} ${local}`;
  else if (dial) s = `+${dial}`;
  else s = local || null;
  if (s && s.length > 100) return s.slice(0, 100);
  return s;
}

const emptyForm = () => ({
  investigatorDescription: '',
  emailID: '',
  phoneDialCode: '',
  phone: '',
  countryNo: '',
  cityNo: '',
  cityDescription: '',
  address1: '',
  address2: '',
  address3: '',
  therapeuticAreaNo: '',
  diseaseNo: '',
  isActive: true,
});

const AddInvestigatorModal = ({ isOpen, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [therapeuticAreas, setTherapeuticAreas] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [dialOpen, setDialOpen] = useState(false);
  const [dialSearch, setDialSearch] = useState('');
  const dialDropdownRef = useRef(null);
  const prevCountryNoForDialRef = useRef(null);

  const dialOptions = useMemo(() => {
    const map = new Map();
    for (const c of countries) {
      const pc = c.phoneCode ?? c.PhoneCode;
      if (pc == null || pc === '') continue;
      const key = String(pc).replace(/\D/g, '');
      if (!key || key === '0') continue;
      if (!map.has(key)) map.set(key, { phoneCode: key, label: `+${key}` });
    }
    return Array.from(map.values()).sort(
      (a, b) => (Number(a.phoneCode) || 0) - (Number(b.phoneCode) || 0),
    );
  }, [countries]);

  const filteredDialOptions = useMemo(() => {
    const q = dialSearch.replace(/\D/g, '');
    if (!q) return dialOptions;
    return dialOptions.filter((o) => o.phoneCode.includes(q));
  }, [dialOptions, dialSearch]);

  useEffect(() => {
    if (!dialOpen) return;
    const onDoc = (e) => {
      if (dialDropdownRef.current && !dialDropdownRef.current.contains(e.target)) {
        setDialOpen(false);
        setDialSearch('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [dialOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/countries');
        const data = res.data;
        const list = Array.isArray(data) ? data : [];
        if (!cancelled) setCountries(list);
      } catch {
        if (!cancelled) setCountries([]);
      }
      try {
        const [areaRes, diseaseRes] = await Promise.all([
          api.get('/therapeutic-areas'),
          api.get('/diseases'),
        ]);
        if (cancelled) return;
        const areaData = areaRes.data;
        setTherapeuticAreas(Array.isArray(areaData) ? areaData : []);
        const diseaseData = diseaseRes.data;
        setDiseases(Array.isArray(diseaseData) ? diseaseData : []);
      } catch {
        if (!cancelled) {
          setTherapeuticAreas([]);
          setDiseases([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !form.countryNo) {
      setCities([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/cities', { params: { countryNo: form.countryNo } });
        const data = res.data;
        const list = Array.isArray(data) ? data : [];
        if (!cancelled) setCities(list);
      } catch {
        if (!cancelled) setCities([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, form.countryNo]);

  // Default phone dial code from CountryMst (nPhonecode) when Country selection changes — not on every countries[] refresh
  useEffect(() => {
    if (!isOpen) {
      prevCountryNoForDialRef.current = null;
      return;
    }
    if (!form.countryNo || !countries.length) {
      if (!form.countryNo) prevCountryNoForDialRef.current = null;
      return;
    }
    const key = String(form.countryNo);
    if (prevCountryNoForDialRef.current === key) return;
    prevCountryNoForDialRef.current = key;
    const c = countries.find((x) => String(x.countryNo) === key);
    const pc = c?.phoneCode ?? c?.PhoneCode;
    if (pc == null || String(pc).replace(/\D/g, '') === '') return;
    const next = String(pc).replace(/\D/g, '');
    setForm((prev) => ({ ...prev, phoneDialCode: next }));
  }, [isOpen, form.countryNo, countries]);

  const selectDialCode = useCallback((code) => {
    setForm((prev) => ({ ...prev, phoneDialCode: code }));
    setDialOpen(false);
    setDialSearch('');
    setFieldErrors((prev) => ({ ...prev, phoneDialCode: '', phone: '' }));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'countryNo') {
      setForm((prev) => ({
        ...prev,
        countryNo: value,
        cityNo: '',
        cityDescription: '',
      }));
      setError('');
      setFieldErrors((prev) => ({ ...prev, countryNo: '', cityNo: '' }));
      return;
    }
    if (name === 'therapeuticAreaNo') {
      setForm((prev) => ({
        ...prev,
        therapeuticAreaNo: value,
        diseaseNo: '',
      }));
      setError('');
      setFieldErrors((prev) => ({ ...prev, therapeuticArea: '', disease: '' }));
      return;
    }
    if (name === 'cityNo') {
      const city = cities.find((c) => String(c.cityNo) === String(value));
      setForm((prev) => ({
        ...prev,
        cityNo: value,
        cityDescription: city?.cityDescription || '',
      }));
      setError('');
      setFieldErrors((prev) => ({ ...prev, cityNo: '' }));
      return;
    }
    if (name === 'phone') {
      const digitsOnly = String(value).replace(/\D/g, '');
      setForm((prev) => ({ ...prev, phone: digitsOnly }));
      setError('');
      setFieldErrors((prev) => ({ ...prev, phone: '', phoneDialCode: '' }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
    if (name) setFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleClose = () => {
    if (saving) return;
    setDialOpen(false);
    setDialSearch('');
    prevCountryNoForDialRef.current = null;
    setForm(emptyForm());
    setError('');
    setFieldErrors({});
    onClose();
  };

  const handleClear = () => {
    if (saving) return;
    setDialOpen(false);
    setDialSearch('');
    prevCountryNoForDialRef.current = null;
    setForm(emptyForm());
    setError('');
    setFieldErrors({});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const description = (form.investigatorDescription || '').trim();
    const email = (form.emailID || '').trim();
    const address1 = (form.address1 || '').trim();
    const cityNoNum = parseInt(String(form.cityNo || '').trim(), 10);
    const selectedArea = therapeuticAreas.find((x) => String(x.id) === String(form.therapeuticAreaNo));
    const selectedDisease = diseases.find((x) => String(x.id) === String(form.diseaseNo));
    const therapeutic = (selectedArea?.name || '').trim();
    const disease = (selectedDisease?.name || '').trim();

    const errs = {};
    if (!description) errs.investigatorDescription = 'Please enter Investigator Name.';
    if (!email) errs.emailID = 'Please enter Email Id.';
    if (!address1) errs.address1 = 'Please enter Address1.';
    if (!Number.isFinite(cityNoNum) || cityNoNum <= 0) errs.cityNo = 'Please select City.';
    if (!form.countryNo) errs.countryNo = 'Please select Country.';
    if (!therapeutic) errs.therapeuticArea = 'Please select Therapeutic Area.';
    if (!disease) errs.disease = 'Please select Disease.';
    const localDigits = String(form.phone || '').replace(/\D/g, '');
    const dialDigits = String(form.phoneDialCode || '').replace(/\D/g, '');
    if (localDigits && !dialDigits) {
      errs.phoneDialCode = 'Please select a country code for the phone number.';
    }
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    void executeSaveAfterAuth();
  };

  const executeSaveAfterAuth = async () => {
    const description = (form.investigatorDescription || '').trim();
    const email = (form.emailID || '').trim();
    const address1 = (form.address1 || '').trim();
    const cityNoNum = parseInt(String(form.cityNo || '').trim(), 10);
    const countryNoNum = form.countryNo ? parseInt(String(form.countryNo).trim(), 10) : null;
    const selectedArea = therapeuticAreas.find((x) => String(x.id) === String(form.therapeuticAreaNo));
    const selectedDisease = diseases.find((x) => String(x.id) === String(form.diseaseNo));
    const therapeutic = (selectedArea?.name || '').trim();
    const disease = (selectedDisease?.name || '').trim();
    const selectedCountry = countries.find(
      (c) => String(c.countryNo) === String(form.countryNo)
    );
    const vPhone = buildVPhone(form.phoneDialCode, form.phone);

    setSaving(true);
    setError('');
    try {
      const res = await api.post('/investigators', {
        investigatorDescription: description,
        emailID: email,
        phone: vPhone,
        address1,
        address2: (form.address2 || '').trim() || null,
        address3: (form.address3 || '').trim() || null,
        cityNo: cityNoNum,
        cityDescription: (form.cityDescription || '').trim() || null,
        therapeuticArea: therapeutic,
        therapeuticAreaNo: selectedArea ? Number(selectedArea.id) : null,
        disease,
        diseaseNo: selectedDisease ? Number(selectedDisease.id) : null,
        countryNo: Number.isFinite(countryNoNum) ? countryNoNum : null,
        countryNicename: selectedCountry?.countryNiceName || selectedCountry?.countryName || null,
        isActive: form.isActive,
      });
      const created = res?.data || null;
      setForm(emptyForm());
      if (typeof onSaved === 'function') onSaved(created);
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to save investigator.';
      setError(typeof msg === 'string' ? msg : 'Failed to save investigator.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Investigator"
      size="medium"
      className="add-investigator-modal"
      overlayClassName="site-feasibility-investigator-overlay"
      footer={
        <>
          <button type="submit" form="add-investigator-form" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleClear} disabled={saving}>
            Clear
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </button>
        </>
      }
    >
      <form id="add-investigator-form" className="add-investigator-form" onSubmit={handleSubmit}>
        {error && <Alert type="error" message={error} />}
        <div className="add-investigator-grid">
          <div className="form-group">
            <label className="form-label">
              Investigator Name <span className="text-danger">*</span>
            </label>
            <input
              className="form-input"
              name="investigatorDescription"
              value={form.investigatorDescription}
              onChange={handleChange}
              disabled={saving}
            />
            {fieldErrors.investigatorDescription && (
              <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
                {fieldErrors.investigatorDescription}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">
              Email Id <span className="text-danger">*</span>
            </label>
            <input
              className="form-input"
              name="emailID"
              type="email"
              value={form.emailID}
              onChange={handleChange}
              autoComplete="email"
              disabled={saving}
            />
            {fieldErrors.emailID && (
              <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
                {fieldErrors.emailID}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Phone No/Code</label>
            <div className="investigator-phone-composite" ref={dialDropdownRef}>
              <div className={`investigator-phone-dial-wrap ${dialOpen ? 'is-open' : ''}`}>
                <button
                  type="button"
                  className="investigator-phone-dial-trigger"
                  aria-expanded={dialOpen}
                  aria-haspopup="listbox"
                  disabled={saving}
                  onClick={() => {
                    setDialOpen((o) => !o);
                    if (!dialOpen) setDialSearch('');
                  }}
                >
                  {form.phoneDialCode ? `+${form.phoneDialCode}` : 'Code'}
                  <span className="investigator-phone-dial-caret" aria-hidden />
                </button>
                {dialOpen && (
                  <div className="investigator-phone-dial-panel" role="listbox">
                    <input
                      type="text"
                      className="investigator-phone-dial-search"
                      placeholder="Search.."
                      value={dialSearch}
                      onChange={(e) => setDialSearch(e.target.value)}
                      autoComplete="off"
                    />
                    <ul className="investigator-phone-dial-list">
                      {filteredDialOptions.length === 0 ? (
                        <li className="investigator-phone-dial-empty">No matches</li>
                      ) : (
                        filteredDialOptions.map((opt) => (
                          <li key={opt.phoneCode}>
                            <button
                              type="button"
                              className="investigator-phone-dial-option"
                              onClick={() => selectDialCode(opt.phoneCode)}
                            >
                              {opt.label}
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <input
                className="form-input investigator-phone-local"
                name="phone"
                type="text"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="Phone number"
                value={form.phone}
                onChange={handleChange}
                disabled={saving}
              />
            </div>
            {(fieldErrors.phoneDialCode || fieldErrors.phone) && (
              <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
                {fieldErrors.phoneDialCode || fieldErrors.phone}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">
              Country <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              name="countryNo"
              value={form.countryNo}
              onChange={handleChange}
              disabled={saving}
            >
              <option value="">Select Country</option>
              {countries.map((c) => (
                <option key={c.countryNo} value={c.countryNo}>
                  {c.countryNiceName || c.countryName}
                </option>
              ))}
            </select>
            {fieldErrors.countryNo && (
              <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
                {fieldErrors.countryNo}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">
              City <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              name="cityNo"
              value={form.cityNo}
              onChange={handleChange}
              disabled={saving || !form.countryNo}
            >
              <option value="">{form.countryNo ? 'Select city' : 'Please select Country first'}</option>
              {cities.map((c) => (
                <option key={c.cityNo} value={c.cityNo}>
                  {c.cityDescription}
                </option>
              ))}
            </select>
            {fieldErrors.cityNo && (
              <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
                {fieldErrors.cityNo}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">
              Address1 <span className="text-danger">*</span>
            </label>
            <input className="form-input" name="address1" value={form.address1} onChange={handleChange} disabled={saving} />
            {fieldErrors.address1 && (
              <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
                {fieldErrors.address1}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Address2</label>
            <input className="form-input" name="address2" value={form.address2} onChange={handleChange} disabled={saving} />
          </div>
          <div className="form-group">
            <label className="form-label">Address3</label>
            <input className="form-input" name="address3" value={form.address3} onChange={handleChange} disabled={saving} />
          </div>
          <div className="form-group">
            <label className="form-label">
              Therapeutic Area <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              name="therapeuticAreaNo"
              value={form.therapeuticAreaNo}
              onChange={handleChange}
              disabled={saving}
            >
              <option value="">Select Therapeutic Area</option>
              {therapeuticAreas.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            {fieldErrors.therapeuticArea && (
              <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
                {fieldErrors.therapeuticArea}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">
              Disease <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              name="diseaseNo"
              value={form.diseaseNo}
              onChange={handleChange}
              disabled={saving || !form.therapeuticAreaNo}
            >
              <option value="">
                {form.therapeuticAreaNo ? 'Select Disease' : 'Select Therapeutic Area first'}
              </option>
              {diseases
                .filter((item) => String(item.therapeuticAreaNo) === String(form.therapeuticAreaNo))
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </select>
            {fieldErrors.disease && (
              <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
                {fieldErrors.disease}
              </div>
            )}
          </div>
          <div className="form-group form-group-checkbox">
            <label className="form-label">
              <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} disabled={saving} />
              <span style={{ marginLeft: 8 }}>Active</span>
            </label>
          </div>
        </div>
      </form>
    </Modal>
    </>
  );
};

export default AddInvestigatorModal;
